import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

// ** Third Party Components
import { CloseCircle } from "iconsax-reactjs"
import Skeleton from "react-loading-skeleton"
import { format, isToday, isYesterday } from "date-fns"
import { useInfiniteQuery } from "@tanstack/react-query"

// ** Helpers
import { cn, parseMessage } from "@pma/utils/helpers"

// ** Constants
import { notificationTabList } from "@pma/utils/constants"

// ** Services
import { getActivitiesListService } from "@pma/services/activities"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Types
import { DictType } from "@pma/types"

// ** Theme Components
import { Switch } from "@pma/components/common/switch"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@pma/components/common/sheet"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import UpdateLogCard from "./UpdateLogCard"

interface UpdateLogsDialogProps {
  updateLogsDialog: boolean
  setUpdateLogsDialog: React.Dispatch<React.SetStateAction<boolean>>
  project_id?: string
}

const UpdateLogsDialog = ({
  updateLogsDialog,
  setUpdateLogsDialog,
  project_id,
}: UpdateLogsDialogProps) => {
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const sheetSide = isHebrew ? "left" : "right"
  const organizationUsers = authConfig?.organizationUsers
  const organization_id = authConfig?.last_organization_id

  const observerRef = useRef<HTMLDivElement | null>(null)

  const [activeTab, setActiveTab] = useState("all")
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [isOpen, setIsOpen] = useState(updateLogsDialog)

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage, refetch } =
    useInfiniteQuery({
      queryKey: ["get-activity-list", unreadOnly, project_id, activeTab],
      queryFn: ({ pageParam = 1 }) =>
        getActivitiesListService({
          readed: unreadOnly,
          paging_per_page: 50,
          type: activeTab,
          paging_start_page: pageParam,
          organization_id,
          ...(project_id && { project_id }),
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage?.nextPage) return undefined
        return allPages.length + 1
      },
      refetchOnMount: "always",
      retry: 0,
    })

  const updateLogsList = useMemo(() => {
    const temp: any = []
    data?.pages.forEach((page) => {
      page.items.forEach((item: any) => {
        let title = "---"
        let note = false
        let valueKeys: any[] = []
        const breadcrumbsList: any = []

        const changes: DictType = item?.changes?.changes ? item.changes.changes[0] || {} : {}
        const prevValue = changes?.prev_value
        const newValue = changes?.new_value

        if (prevValue && Object.keys(prevValue).length > 0) {
          valueKeys = [...Object.keys(prevValue)]
        }
        if (newValue && Object.keys(newValue)?.length > 0) {
          valueKeys = [...Object.keys(newValue)]
        }

        if (valueKeys.includes("title")) {
          const condition = item?.changes?.changes[0]?.change_action?.en
          if (condition == "subtask added") {
            title = `${translate("Subtask was created")}: ${newValue?.title}`
          } else if (condition == "dependent task added") {
            title = `${translate("Task was edited")}: ${translate("Dependent task added")} - ${newValue?.title}`
          } else if (condition == '"depend on" task added') {
            title = `${translate("Task was edited")}: ${translate("depend on")} - ${newValue?.title}`
          } else if (condition == "dependent task deleted") {
            title = `${translate("Task was edited")}: ${translate("Dependent task deleted")} - ${prevValue?.title}`
          } else if (condition == '"depend on" task deleted') {
            title = `${translate("Task was edited")}: ${translate("depend on deleted ")} - ${prevValue?.title}`
          } else if (condition == "subtask deleted") {
            title = `${translate("Subtask was deleted")}: ${prevValue?.title}`
          } else {
            title = `${translate("Task title was changed from")} ${prevValue?.title} ${translate("to")} ${newValue?.title}`
          }
        } else if (valueKeys.includes("construction_name")) {
          if (item?.changes?.changes[0]?.change_action?.en == "edit project") {
            title = `${translate("Project Construction name was changed from")} ${prevValue.construction_name} ${translate("to")} ${newValue.construction_name}`
          }
        } else if (changes?.column_name === "responsible_id") {
          if (Object.keys(newValue).length > 0)
            title = `${translate("Task assigned to")} ${newValue?.name}`
          else if (Object.keys(prevValue).length > 0)
            title = `${translate("Remove assigned to")} ${prevValue?.name}`
        } else if (valueKeys.includes("note") || valueKeys.includes("Note")) {
          const condition = item?.changes?.changes[0]?.change_action?.en
          if (item?.changes?.changes[0]?.change_action?.en == "note was added") {
            title = `${translate("Note was created")}: ${parseMessage(newValue?.note, organizationUsers)}`
          } else if (condition == "note was deleted") {
            title = `${translate("Note was deleted")}:  ${parseMessage(prevValue?.note, organizationUsers)}`
          } else {
            title = `${translate("Note text was edited from")} ${parseMessage(prevValue?.note, organizationUsers)} ${translate("to")} ${parseMessage(newValue?.note, organizationUsers)}`
          }
          note = true
        } else if (valueKeys.includes("file")) {
          title = `${translate("New file was added")}.`
        } else if (valueKeys.includes("finish_to") || valueKeys.includes("start_at")) {
          const changesObj = item.changes?.changes ? item.changes?.changes[0] : null
          if (changesObj) {
            const changeAction = changesObj.change_action
            if (changeAction[locale] === "start time was changed") {
              const oldTime = changesObj.prev_value.start_at
                ? new Date(changesObj.prev_value.start_at)
                : null
              const newTime = changesObj.new_value?.start_at
                ? new Date(changesObj.new_value?.start_at)
                : null
              if (oldTime && newTime) {
                const fromDate = `${oldTime.getDate()}/${oldTime.getMonth() + 1}/${oldTime.getFullYear()}`
                const toDate = `${newTime.getDate()}/${newTime.getMonth() + 1}/${newTime.getFullYear()}`
                title = `${translate("Task start time was changed from")} ${fromDate} ${translate("to")} ${toDate}`
              }
            } else if (changeAction[locale] === "finish time was changed") {
              const oldTime = changesObj.prev_value.finish_to
                ? new Date(changesObj.prev_value.finish_to)
                : null
              const newTime = changesObj.new_value?.finish_to
                ? new Date(changesObj.new_value?.finish_to)
                : null
              if (oldTime && newTime) {
                const fromDate = `${oldTime.getDate()}/${oldTime.getMonth() + 1}/${oldTime.getFullYear()}`
                const toDate = `${newTime.getDate()}/${newTime.getMonth() + 1}/${newTime.getFullYear()}`
                title = `${translate("Task finish time was changed from")} ${fromDate} ${translate("to")} ${toDate}`
              }
            }
          }
        } else if (item.changes?.change_type?.en == "new task") {
          title = `${translate("New task created")}: ${item.changes?._task?.title || item._task?.title}`
        } else if (changes?.column_name === "approval") {
          title = `${translate("Edit work diary")}: ${translate("Approval was changed")}.`
        } else if (valueKeys.includes("description")) {
          title = `${translate("Task description was changed")}: ${newValue?.description}`
        } else if (changes?.column_name === "status_id") {
          if (item.changes?.change_type?.en == "edit work diary") {
            title = `${translate("Work diary status was changed from")} ${prevValue?.en} ${translate("to")} ${newValue?.en}`
          } else {
            title = `${translate("Status was changed from")} ${prevValue?.name} ${translate("to")} ${newValue?.name}`
          }
        } else if (item.changes?.change_type?.en == "new project") {
          title = `${translate("The project was created")}`
        } else if (item.changes?.change_type?.en == "edit project") {
          title = `${translate("The project was edited")}: ${item._project?.title}`
        } else if (item.changes?.change_type?.en == "delete task") {
          title = `${translate("Deleted task")}: ${item?.deleted_object?.title}`
        } else if (item.changes?.change_type?.en == "new work diary") {
          title = `${translate("Work diary was created")}`
        } else if (item.changes?.change_type?.en == "delete work diary") {
          title = `${translate("Work diary was deleted")}: ${item?.deleted_object?.number}`
        } else if (item.changes?.change_type?.en == "edit work diary") {
          if (item?.changes?.changes[0]?.change_action?.en == "address was changed") {
            title = `${translate("Work diary address was changed from")} ${prevValue.address} ${translate("to")} ${newValue.address}`
          }
        } else if (valueKeys.includes("annotation")) {
          const condition = item?.changes?.changes[0]?.change_action?.en
          if (condition == "file annotation was deleted")
            title = `${translate("Annotation image file was deleted")}.`
          else title = `${translate("Annotation image file was modified")}.`
        } else if (item.changes?.change_type?.en == "new bill") {
          title = `${translate("New bill created")}`
        } else if (item.changes?.change_type?.en == "edit bill") {
          title = `${translate("Bill was edited")}`
        } else if (item.changes?.change_type?.en == "mark bill as deleted") {
          title = `${translate("Bill was deleted")}: ${item?.deleted_object?.number}`
        } else {
        }

        // Breadcrumbs Order
        if (Object.keys(item?.breadcrumbs).length) {
          if (item?.breadcrumbs?.project) {
            const permission = {
              is_project_group: false,
              is_project: false,
              is_task: false,
              is_diary: false,
            }
            item?.breadcrumbs?.project?.project_groups.map((group: any) =>
              breadcrumbsList.push({
                ...permission,
                is_project_group: true,
                id: group.group_id,
                title: group.group_name,
              }),
            )
            breadcrumbsList.push({
              ...permission,
              is_project: true,
              id: item?.breadcrumbs?.project.project_id,
              title: item?.breadcrumbs?.project.project_name,
            })
          } else {
            breadcrumbsList.push({
              is_project_group: false,
              is_task: false,
              is_diary: false,
              is_project: true,
              id: item?.breadcrumbs?.project_id,
              title: item?.breadcrumbs?.project_name,
            })
          }

          if (item?.breadcrumbs?.task) {
            const permission = {
              is_project: false,
              is_task: true,
              is_diary: false,
              is_project_group: false,
            }
            if (Object.keys(item?.breadcrumbs?.task?.tasks_group).length)
              breadcrumbsList.push({
                ...permission,
                id: item?.breadcrumbs?.task.task_id,
                title: item?.breadcrumbs?.task.tasks_group?.tasks_group_name,
              })

            if (Object.keys(item?.breadcrumbs?.task?.parent_task).length)
              breadcrumbsList.push({
                ...permission,
                id: item?.breadcrumbs?.task?.parent_task?.parent_tasks_id,
                title: item?.breadcrumbs?.task?.parent_task?.parent_tasks_number,
              })

            breadcrumbsList.push({
              ...permission,
              id: item?.breadcrumbs?.task.task_id,
              title: item?.breadcrumbs?.task.task_number,
            })
          } else {
          }

          if (item?.breadcrumbs?.diary) {
            if (Object.keys(item?.breadcrumbs?.diary).length) {
              breadcrumbsList.push({
                is_project: false,
                is_task: false,
                is_diary: true,
                is_project_group: false,
                id: item?.breadcrumbs?.diary.diary_id,
                title: item?.breadcrumbs?.diary.diary_number,
              })
            }
          }
        }

        temp.push({
          id: item.id,
          title: title,
          note: note,
          created_at: item?.created_at,
          _is_liked: item?._is_liked,
          _is_read: item?._is_readed,
          liked_by_id: item?.liked_by_id,
          user: item?._user,
          task_id: item?.task_id,
          breadcrumbs: breadcrumbsList,
        })
      })
    })
    return temp
  }, [data, translate, organizationUsers])

  const groupedUpdatedLogs = useMemo(() => {
    const groups: Record<string, any[]> = {}

    updateLogsList.forEach((notification: any) => {
      const date = new Date(notification.created_at)
      let groupLabel = format(date, "PPP") // Default full date format

      if (isToday(date)) groupLabel = translate("Today")
      else if (isYesterday(date)) groupLabel = translate("Yesterday")

      if (!groups[groupLabel]) groups[groupLabel] = []
      groups[groupLabel].push(notification)
    })

    return groups
  }, [updateLogsList, translate])

  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: "0px",
      threshold: 1.0,
    })

    if (observerRef.current) observer.observe(observerRef.current)

    return () => {
      if (observerRef.current) observer.unobserve(observerRef.current)
    }
  }, [observerCallback])

  useEffect(() => {
    if (!isOpen) setUpdateLogsDialog(false)
  }, [isOpen])

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        className={cn(
          "flex w-full flex-col gap-0 rounded-lg p-0 focus:outline-none md:max-w-xl lg:mt-2.5 lg:h-[calc(100vh-20px)]",
          isHebrew ? "rtl lg:ml-2.5" : "ltr lg:mr-2.5",
        )}
        side={sheetSide}
        hideCloseButton={true}
      >
        <SheetHeader className='flex-row items-center justify-between gap-4 space-y-0 border-b border-neutral-100 px-6 py-4'>
          <SheetTitle className='flex items-center gap-4'>
            <p className='font-[inter] text-base font-normal'>{translate("Update logs")}</p>
          </SheetTitle>
          <CloseCircle size={20} className='cursor-pointer' onClick={() => setIsOpen(false)} />
        </SheetHeader>

        <SheetDescription className='flex h-full flex-col overflow-y-auto'>
          <div className='flex items-center justify-between border-b border-neutral-200'>
            <div className='flex'>
              {notificationTabList.map((tab) => (
                <div
                  className={cn(
                    "flex cursor-pointer flex-col gap-3 px-4 py-3",
                    activeTab === tab.value && "border-b-2 border-neutral-900",
                  )}
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                >
                  <p
                    className={cn(
                      "font-[inter] text-base",
                      activeTab === tab.value
                        ? "font-medium text-neutral-800"
                        : "font-normal text-neutral-600",
                    )}
                  >
                    {translate(tab.label)}
                  </p>
                </div>
              ))}
            </div>
            <div className='flex pr-4'>
              <div className='flex items-center gap-2 px-4 py-3'>
                <label className='font-[inter] text-sm font-normal text-indigo-600'>
                  {translate("Unread only")}
                </label>
                <Switch className='h-6 w-11' onCheckedChange={setUnreadOnly} />
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-4 rounded-2xl p-4'>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  className='flex flex-col gap-4 rounded-2xl border border-neutral-200 p-4'
                >
                  <div className='flex gap-3'>
                    <Skeleton circle height={24} width={24} />
                    <div className='flex-1'>
                      <Skeleton width={`60%`} height={16} />
                      <Skeleton width={`40%`} height={12} style={{ marginTop: 6 }} />
                    </div>
                  </div>
                  <Skeleton count={2} height={12} style={{ marginTop: 6 }} />
                </div>
              ))
            ) : updateLogsList.length === 0 ? (
              <div className='flex h-full w-full items-center justify-center py-20'>
                <p className='font-[inter] text-sm text-neutral-500'>
                  {translate("No records found")}
                </p>
              </div>
            ) : (
              Object.entries(groupedUpdatedLogs).map(([group, updateLogs]) => (
                <div key={group} className='flex flex-col gap-4'>
                  <p className='font-[inter] text-[10px] font-normal text-neutral-500'>{group}</p>
                  {updateLogs.map((log) => (
                    <UpdateLogCard
                      key={log.id}
                      log={log}
                      onRefetch={refetch}
                    />
                  ))}
                </div>
              ))
            )}

            {hasNextPage && (
              <div ref={observerRef} className='flex flex-col gap-2 pt-4'>
                {isFetchingNextPage && (
                  <div className='col-span-full flex items-center justify-center py-4'>
                    <Skeleton height={20} width={100} />
                  </div>
                )}
              </div>
            )}
          </div>
        </SheetDescription>
      </SheetContent>
    </Sheet>
  )
}

export default UpdateLogsDialog
